import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import AppHeader from '@/components/AppHeader';

interface AdminDashboardProps {
  onSignOut: () => void;
}

export default function AdminDashboard({ onSignOut }: AdminDashboardProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [deployInfo, setDeployInfo] = useState<{ version?: string; deployedAt?: string; env?: string } | null>(null);

  useEffect(() => {
    fetch('/deploy-info.json')
      .then(r => r.json())
      .then(setDeployInfo)
      .catch(() => {});
  }, []);

  const cards = [
    {
      title: 'Team Management',
      description: 'Create, edit, and delete teams',
      path: '/admin/teams',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      color: 'text-team-a',
    },
    {
      title: 'Player Management',
      description: 'Manage players and assign to teams',
      path: '/admin/players',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
      color: 'text-team-b',
    },
    {
      title: 'Tournament Management',
      description: 'Create and manage tournaments',
      path: '/tournaments',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 4h14" /><path d="M5 4v6a7 7 0 0 0 14 0V4" />
          <line x1="12" y1="16" x2="12" y2="20" />
          <line x1="9" y1="20" x2="15" y2="20" />
          <path d="M9 20a4 4 0 0 0-4 4" />
          <path d="M15 20a4 4 0 0 1 4 4" />
        </svg>
      ),
      color: 'text-success',
    },
    {
      title: 'Match Management',
      description: 'View, delete, and clean up match data',
      path: '/admin/matches',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
        </svg>
      ),
      color: 'text-error',
    },
    {
      title: 'Club Settings',
      description: 'Upload and manage club branding',
      path: '/admin/club',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>
      ),
      color: 'text-warning',
    },
    {
      title: 'User Management',
      description: 'Manage users, roles, and passwords',
      path: '/admin/users',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      color: 'text-warning',
    },
    {
      title: 'Rally Editor',
      description: 'Correct rally details — point winner, lost reason',
      path: '/admin/rally-editor',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      ),
      color: 'text-primary',
    },
    {
      title: 'Broadcast Control',
      description: 'Set the live broadcast overlay',
      path: '/admin/broadcast',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
          <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
          <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
          <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
        </svg>
      ),
      color: 'text-error',
    },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader onSignOut={onSignOut} title="Admin Dashboard" navIcon="home" onNavigate={() => navigate('/')} />

      <div className="flex-1 overflow-y-auto px-4 py-6 safe-area-bottom max-w-md mx-auto w-full">
        {/* Current User Info */}
        {profile && (
          <div className="card mb-6 bg-surface-raised">
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">
              Signed in as
            </p>
            <p className="text-base font-bold text-text-primary">{profile.displayName}</p>
            <p className="text-xs text-text-muted uppercase tracking-wide mt-0.5">{profile.role}</p>
          </div>
        )}

        {/* Navigation Cards */}
        <div className="space-y-3">
          {cards.map((card) => (
            <button
              key={card.path}
              onClick={() => navigate(card.path)}
              className="w-full card flex items-center gap-4 text-left hover:border-team-a/50 transition-colors active:scale-[0.99]"
            >
              <div className={card.color}>{card.icon}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-text-primary">{card.title}</p>
                <p className="text-xs text-text-muted">{card.description}</p>
              </div>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted flex-shrink-0">
                <path d="M8 4l6 6-6 6" />
              </svg>
            </button>
          ))}
        </div>

        {/* Release info */}
        {deployInfo && (
          <div className="mt-6 pt-4 border-t border-border text-center space-y-0.5">
            {deployInfo.version && (
              <p className="text-xs text-text-muted">
                v{deployInfo.version}{deployInfo.env ? ` · ${deployInfo.env}` : ''}
              </p>
            )}
            {deployInfo.deployedAt && (
              <p className="text-xs text-text-muted">
                Deployed {new Date(deployInfo.deployedAt).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
